// App composition — router-agnostic. The HOST supplies the Router: the desktop
// renderer uses HashRouter (required for Electron's file:// protocol); the cloud
// web app (apps/cloud-web) uses BrowserRouter over this same <AppRoutes/> tree.
// The host wraps <AppRoutes/> with its Router and passes it as children to
// <AppProviders/> — keeping the provider stack and the route tree shared across
// both surfaces while the Router choice stays a host concern.
import { Suspense, lazy, useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import { CommandPalette } from '@/components/CommandPalette';
import { AuthTokenGate } from '@/components/AuthTokenGate';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@/components/ThemeProvider';
import { AppToaster } from '@/components/AppToaster';
import { Layout } from '@/components/layout/Layout';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { useIsMobile } from '@/lib/useIsMobile';
import { useLiveEvents } from '@/lib/useLiveEvents';
import { noteRoute } from '@/core/performanceObserver';
import { LoadingState } from '@onestone/ui/loading-state';

// Lazy-load all page components — order matches nav.
const RoutinesPage = lazy(() => import('@/pages/RoutinesPage').then((m) => ({ default: m.RoutinesPage })));
const EmailPage = lazy(() => import('@/pages/EmailPage').then((m) => ({ default: m.EmailPage })));
const ContactsPage = lazy(() => import('@/pages/ContactsPage').then((m) => ({ default: m.ContactsPage })));
const AgentsPage = lazy(() => import('@/pages/AgentsPage').then((m) => ({ default: m.AgentsPage })));
const CalendarPage = lazy(() => import('@/pages/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const SettingsPage = lazy(() => import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const ConnectionsPage = lazy(() => import('@/pages/ConnectionsPage').then((m) => ({ default: m.ConnectionsPage })));
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const KnowledgePage = lazy(() => import('@/pages/KnowledgePage').then((m) => ({ default: m.KnowledgePage })));
const OnboardingPage = lazy(() => import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Index landing — chat-first on phones (single-page shell), projects on
 *  desktop (chat visible in the split panel). */
function HomeRedirect() {
  const isMobile = useIsMobile();
  return <Navigate to={isMobile ? '/chat' : '/projects'} replace />;
}

/** /chat — the full-page chat is the phone's home screen. Desktop already shows
 *  the chat beside every page, so a full-page copy there would put two chat
 *  panels side by side; that route goes to the default page instead. */
function ChatRoute() {
  const isMobile = useIsMobile();
  if (!isMobile) return <Navigate to="/projects" replace />;
  return (
    <ErrorBoundary>
      <ChatPanel compact />
    </ErrorBoundary>
  );
}

/** Subscribes to app-wide live store-change events for the session. Renders nothing. */
function LiveEventsBridge() {
  useLiveEvents();
  return null;
}

/** The route tree. Router-agnostic — must be rendered inside a Router (Hash on
 *  desktop, Browser on web) supplied by the host. */
export function AppRoutes() {
  const { pathname } = useLocation();
  // Layout effect: runs in the commit of the frame that renders the route, so
  // the perf monitor attributes that frame to the route it shows.
  useLayoutEffect(() => noteRoute(pathname), [pathname]);
  return (
    <ErrorBoundary resetKey={pathname}>
      <CommandPalette />
      <Suspense fallback={<LoadingState variant="centered" />}>
        <Routes>
          <Route path="/onboarding" element={<OnboardingPage />} />

          <Route element={<Layout />}>
            <Route index element={<HomeRedirect />} />
            {/* ERR-M2: the chat pane gets its own boundary on every layout
                (ChatRoute on phones, Layout.tsx on desktop) — streaming
                chunks have mutable shapes and are the highest-risk render-crash
                source. A crash here stays contained to the chat pane; the
                nav and other routes remain usable. */}
            <Route path="/chat" element={<ChatRoute />} />
            <Route path="/routines" element={<RoutinesPage />} />
            <Route path="/email" element={<EmailPage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/connections" element={<ConnectionsPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/projects" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

/** The shared provider stack (QueryClient, theme, toaster, auth gate, live
 *  events). Router-agnostic — the host renders its Router-wrapped <AppRoutes/>
 *  as {children}. */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppToaster />
        <AuthTokenGate>
          <LiveEventsBridge />
          {children}
        </AuthTokenGate>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
